import { useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import ImageDropZone from '../../components/image/ImageDropZone';

const KIND_CONFIG = {
    photo: {
        aspect: 1,
        initialWidthPct: 70,
        dropTitleKey: 'govtJobPhotoSign.photoDropTitle',
        dropHintKey: 'govtJobPhotoSign.photoDropHint',
        sizeKey: 'govtJobPhotoSign.photoSize',
    },
    signature: {
        aspect: 300 / 80,
        initialWidthPct: 90,
        dropTitleKey: 'govtJobPhotoSign.signatureDropTitle',
        dropHintKey: 'govtJobPhotoSign.signatureDropHint',
        sizeKey: 'govtJobPhotoSign.signatureSize',
    },
};

function pixelCropFromPercent(pct, width, height) {
    return {
        unit: 'px',
        x: (pct.x / 100) * width,
        y: (pct.y / 100) * height,
        width: (pct.width / 100) * width,
        height: (pct.height / 100) * height,
    };
}

export default function GovtJobCropPanel({
    kind,
    file,
    isDragging,
    onPick,
    onDrop,
    onDragOver,
    onDragLeave,
    onRemove,
    onCropStateChange,
}) {
    const { t } = useTranslation();
    const config = KIND_CONFIG[kind];
    const imgRef = useRef(null);
    const [src, setSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);

    useEffect(() => {
        if (!file) {
            setSrc('');
            setCrop(undefined);
            setCompletedCrop(null);
            onCropStateChange?.(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setSrc(url);
        setCrop(undefined);
        setCompletedCrop(null);
        return () => URL.revokeObjectURL(url);
    }, [file, onCropStateChange]);

    useEffect(() => {
        if (!imgRef.current || !completedCrop?.width) {
            onCropStateChange?.(null);
            return;
        }
        onCropStateChange?.({ image: imgRef.current, crop: completedCrop });
    }, [completedCrop, onCropStateChange]);

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const pct = centerCrop(
            makeAspectCrop(
                { unit: '%', width: config.initialWidthPct },
                config.aspect,
                width,
                height,
            ),
            width,
            height,
        );
        setCrop(pct);
        setCompletedCrop(pixelCropFromPercent(pct, width, height));
    };

    if (!file) {
        return (
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                        {t(config.sizeKey)}
                    </p>
                </div>
                <ImageDropZone
                    title={t(config.dropTitleKey)}
                    hint={t(config.dropHintKey)}
                    isDragging={isDragging}
                    onClick={onPick}
                    onKeyDown={(e) => e.key === 'Enter' && onPick?.()}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                />
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                    {t(config.sizeKey)}
                </p>
                <button
                    type="button"
                    onClick={onRemove}
                    className="rounded-lg border border-[#ECE8E4] px-2.5 py-1 text-[11.5px] font-semibold text-[#6B635C] transition-colors hover:border-red-200 hover:text-red-500 dark:border-zinc-700 dark:text-zinc-300"
                >
                    {t('govtJobPhotoSign.removeImage')}
                </button>
            </div>
            <div className="overflow-hidden rounded-[14px] border border-[#ECE8E4] bg-[#1C1815] dark:border-zinc-700">
                {src ? (
                    <ReactCrop
                        crop={crop}
                        onChange={(next) => setCrop(next)}
                        onComplete={(next) => setCompletedCrop(next)}
                        aspect={config.aspect}
                        className="max-h-[280px]"
                    >
                        <img
                            ref={imgRef}
                            src={src}
                            alt={t(config.sizeKey)}
                            onLoad={onImageLoad}
                            className="max-h-[280px] w-full object-contain"
                            draggable={false}
                        />
                    </ReactCrop>
                ) : null}
            </div>
            <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">{t('govtJobPhotoSign.cropHint')}</p>
        </div>
    );
}
